import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { CountriesService } from '@core/services/countries.service';
import { PortalDeterminationService } from '@core/services/portal-determination.service';
import { SpinnerService } from '@core/services/spinner.service';
import { APIAdminClient } from '@core/typings/api/admin-client.typing';
import { AdaptedNppSearchResult, ApplicantOrganizationForUi, BucketSearchResultObj, OrgUnion, SearchResultType } from '@core/typings/organization.typing';
import { ProcessingTypes } from '@core/typings/payment.typing';
import { AllowedLocation } from '@core/typings/program.typing';
import { AddOrgUI } from '@core/typings/ui/add-org.typing';
import { AddOrganizationService } from '@features/add-organization/add-organization.service';
import { OrgNotEligibleModalComponent } from '@features/add-organization/org-not-eligible-modal/org-not-eligible-modal.component';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { NonprofitService } from '@features/nonprofit/nonprofit.service';
import { ALL_SKIP_FILTER, APIResult, AddressFormatterService, DebounceFactory, OrganizationEligibleForGivingStatus, OrganizationSearchResponse, PaginationOptions, SearchResult, Tab, TableDataFactory, TableRepository, TableRepositoryFactory, TypeToken } from '@yourcause/common';
import { TypeSafeFormBuilder, TypeSafeFormGroup, TypeaheadSelectOption } from '@yourcause/common/core-forms';
import { HeapService, TrackingEventNames, TrackingPropertyNames } from '@yourcause/common/heap';
import { I18nService } from '@yourcause/common/i18n';
import { ModalFactory, YCModalComponent } from '@yourcause/common/modals';
import { Subscription } from 'rxjs';
interface SearchAllOrgsFormGroup {
  term: string;
  countryCode: string;
  stateProvRegCode: string;
}
enum SearchTabType {
  Standard = 0,
  Client = 1
}

@Component({
    selector: 'gc-search-all-orgs',
    templateUrl: './search-all-orgs-modal.component.html',
    styleUrls: ['./search-all-orgs-modal.component.scss'],
    standalone: false
})
export class SearchAllOrgsModalComponent extends YCModalComponent<{
  selectedOrg: SearchResult;
  isPrivate: boolean;
}> implements OnInit, OnDestroy, AfterViewInit {
  @Input() hideAddOrg: boolean;
  @Input() saveNewlyCreatedOrgs: boolean;
  @Input() orgSearchGuidelines: string;
  @Input() isNomination = false;
  @Input() exitRouterLink: string; // Pass in for action to exit modal and go to specified route
  @Input() exitLinkText: string;
  @Input() processorType: ProcessingTypes;
  @Input() favoriteOrgs: ApplicantOrganizationForUi[] = [];
  @Input() charityBucketId: string;
  @Input() clientId: number;
  @Input() clientName: string;
  @Input() forceOrgSelect: boolean;
  @Input() allowedOrgLocations: AllowedLocation[];
  @Input() programId: number;
  @ViewChildren('orgSearchResults') orgSearchResults: QueryList<ElementRef>;
  @ViewChildren('favOrgSearchResults') favOrgSearchResults: QueryList<ElementRef>;

  tabs: Tab[] = [];
  ProcessingTypes = ProcessingTypes;
  selectedSearchType = SearchTabType.Standard;
  SearchTabs = SearchTabType;
  hasNoResults = true;
  hasNoClientResults = true;
  hasStartedSearching = false;
  clientHasPrivateOrgs = false;
  isFavoriteOrgsView: boolean;
  awaitingNppResults = false;
  useBucketSearch = false;
  hasInternational = this.clientSettingsService.doesClientHaveClientFeature(APIAdminClient.ClientFeatureTypes.HasInternational);
  hide = false;
  formGroup: TypeSafeFormGroup<SearchAllOrgsFormGroup>;
  regIdHelpText = this.i18n.translate(
    'common:textRegIdHelpText2',
    {},
    `The Registration ID is the nonprofit's official ID`
  );
  orgSearchNoResultsText = this.i18n.translate(
    'common:textOrgSearchNoResultsText',
    {},
    `Try checking spelling, spacing, and abbreviations. If we still can't find it, your organization may not be registered with NPOconnect or eligible to apply for this program.`
  );
  orgClientSearchNoResultsText = this.i18n.translate(
    'common:textOrgClientSearchNoResultsText',
    {},
    `Try checking spelling, spacing, and abbreviations.`
  );
  favoriteOrgsHelpText = this.i18n.translate(
    'common:textFavoriteOrgHelpText',
    {},
    `You have previously created applications for the organizations below. Choose one of these and continue to your application or click on "Search organizations" to select another organization.`
  );
  orgSearchRepo: TableRepository<SearchResultType>;
  orgSearchFactory: TableDataFactory<SearchResultType>;
  clientOrgSearchRepo: TableRepository<SearchResultType>;
  clientOrgSearchFactory: TableDataFactory<SearchResultType>;
  favoritesOrgSearchRepo: TableRepository<SearchResultType>;
  $repo = new TypeToken<TableRepository<SearchResultType>>();
  searchAllOrganizationsHelpLinkApplicant = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-applicants-search-tips-for-organizations.html';
  searchAllOrganizationsHelpLinkManager = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-applications-search-tips-for-organizations.html';
  rowsPerPage = 4;
  selectedOrg: SearchResultType;
  loading = false;
  clientLoading = false;
  subs = new Subscription();
  initialSearch = true;
  isManager = this.portal.isManager;
  clientDefaultCountry = this.clientSettingsService.clientSettings.country || 'US';
  countrySelects = [
    {
      label: this.i18n.translate(
        'common:textAllCountries',
        {},
        'All countries'
      ),
      value: ALL_SKIP_FILTER
    },
    ...this.countriesService.countrySelects
  ];
  countryToRegionOptionsMap = this.countriesService.countryCodeToRegionOptionsMap;
  regionSelects: TypeaheadSelectOption[] = [];
  pageChanged = false;

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private autoTableFactory: TableRepositoryFactory,
    private countriesService: CountriesService,
    private i18n: I18nService,
    private nonprofitService: NonprofitService,
    private spinnerService: SpinnerService,
    private clientSettingsService: ClientSettingsService,
    private addOrganizationService: AddOrganizationService,
    private modalFactory: ModalFactory,
    private portal: PortalDeterminationService,
    private addressFormatter: AddressFormatterService,
    private router: Router,
    private heapService: HeapService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  get searchTerm () {
    return this.formGroup.value.term;
  }

  get isClientProcessing () {
    return this.processorType === ProcessingTypes.Client;
  }

  get showNoResults () {
    if (this.isFavoriteOrgsView) {
      return false;
    }
    if (this.selectedSearchType === SearchTabType.Client) {
      return this.hasNoClientResults;
    }

    return this.hasNoResults;
  }

  get showTabs () {
    return this.clientHasPrivateOrgs && !this.hasNoClientResults && this.hasStartedSearching;
  }

  get showAddOrg () {
    return !this.hideAddOrg && this.hasStartedSearching;
  }

  get currentRepo () {
    if (this.isFavoriteOrgsView) {
      return this.favoritesOrgSearchRepo;
    } else if (this.selectedSearchType === SearchTabType.Client) {
      return this.clientOrgSearchRepo;
    }

    return this.orgSearchRepo;
  }

  async ngOnInit () {
    this.useBucketSearch = !!this.charityBucketId;
    if (!this.useBucketSearch) {
      this.spinnerService.startSpinner();
      this.clientHasPrivateOrgs = await this.nonprofitService.checkIfClientHasPrivateOrgs(this.clientId);
      if (this.clientHasPrivateOrgs) {
        this.setTabs();
      }
      this.spinnerService.stopSpinner();
    }
    this.formGroup = this.formBuilder.group<SearchAllOrgsFormGroup>({
      term: '',
      countryCode: this.hasInternational ? ALL_SKIP_FILTER : this.clientDefaultCountry,
      stateProvRegCode: ALL_SKIP_FILTER
    });
    this.setRegionSelects();
    this.hideAddOrg = this.useBucketSearch || this.hideAddOrg;
    this.handleFavorites();
    this.setRepositories();
  }

  ngAfterViewInit () {
    if (this.isFavoriteOrgsView) {
      this.favOrgSearchResults.changes.subscribe(() => {
        if (this.pageChanged) {
          this.favOrgSearchResults.first?.nativeElement?.focus();
          this.pageChanged = false;
        }
      });
    } else {
      this.orgSearchResults.changes.subscribe(() => {
        if (this.pageChanged) {
          this.orgSearchResults.first?.nativeElement?.focus();
          this.pageChanged = false;
        }

        setTimeout(() => {
          // track event
          this.heapService.track(
            TrackingEventNames.OrganizationSearch,
            {
              [TrackingPropertyNames.SearchTerm]: this.searchTerm,
              [TrackingPropertyNames.RecordCount]: this.currentRepo.totalRecords
            }
          );
        }, 0);
      });
    }
  }

  setTabs () {
    this.tabs = [{
      label: this.i18n.translate('common:lblOrganizations', {}, 'Organizations'),
      active: this.selectedSearchType === SearchTabType.Standard
    }, {
      label: this.i18n.translate('common:textOther', {}, 'Other'),
      active: this.selectedSearchType === SearchTabType.Client
    }];
  }

  handleFavorites () {
    if (this.favoriteOrgs?.length > 0 && !this.useBucketSearch) {
      this.favoriteOrgs = this.favoriteOrgs.filter((org) => {
        const isEligible = this.nonprofitService.isOrgEligibleToApplyForProcessing(
          org.eligibleForGivingStatusId,
          org.registrationAuthorityName,
          org.latestVettingRequestStatusForOrg,
          this.isClientProcessing
        );
        const isLocationEligible = this.countriesService.checkIfPassesLocationFiltering(
          org.country,
          org.state,
          this.allowedOrgLocations
        );

        return isEligible && isLocationEligible;
      });

      this.isFavoriteOrgsView = this.favoriteOrgs.length > 0;
    }
  }

  setRepositories () {
    if (this.isFavoriteOrgsView) {
      this.favoritesOrgSearchRepo = this.autoTableFactory.create({
        key: 'FAVORITE_ORGS',
        columns: [],
        rowsPerPage: this.rowsPerPage,
        formGroup: this.formGroup,
        rows: this.favoriteOrgs.map<SearchResultType>((org) => {
          return {
            ...org,
            parentName: '',
            parentId: null,
            parentGuid: null,
            address: this.addressFormatter.formatSimpleGrantsAddressToSingleLine({
              address: org.address,
              address2: org.address2,
              city: org.city,
              state: org.state,
              country: org.country,
              postalCode: org.postalCode
            }),
            registrationId: org.orgIdentification,
            iconURL: org.imageUrl,
            classification: null,
            eligibleForGivingStatusId: org.eligibleForGivingStatusId,
            isPrivateOrg: org.isPrivateOrg,
            isBucketOrg: false
          };
        }),
        skipAddToState: true,
        changeDetectorRef: this.changeDetectorRef
      });
      this.subs.add(this.favoritesOrgSearchRepo.activeResults$.subscribe((records: SearchResultType[]) => {
        this.handleResultsChange(records, null, true);
      }));
    }
    if (this.clientHasPrivateOrgs && !this.useBucketSearch) {
      this.clientOrgSearchFactory = DebounceFactory.createSimple(async (
        options: PaginationOptions<OrganizationSearchResponse>
      ) => {
        const {
        stateProvRegCode,
        countryCode
      } = this.getStateAndCountry();
        const results = await this.nonprofitService.doSearchPrivate(
          options.pageNumber,
          this.searchTerm,
          this.rowsPerPage,
          this.processorType,
          stateProvRegCode,
          countryCode,
          this.clientId,
          this.programId
        );
        this.setSearchHelpers(results, SearchTabType.Client);

        return results;
      }, 1000);
      this.clientOrgSearchRepo = this.autoTableFactory.create({
        key: 'ORG_SEARCH_CLIENT',
        columns: [],
        rowsPerPage: this.rowsPerPage,
        formGroup: this.formGroup,
        tableDataFactory: this.clientOrgSearchFactory,
        skipAddToState: true,
        userInitiatedCalls: true,
        changeDetectorRef: this.changeDetectorRef
      });
      this.subs.add(this.clientOrgSearchRepo.activeResults$.subscribe((records: SearchResultType[]) => {
        this.handleResultsChange(records, SearchTabType.Client);
        // If client results are returned, but NPP has none, we should switch tabs to display the client results
        if (!this.hasNoClientResults && this.selectedSearchType === SearchTabType.Standard) {
          if (this.loading) {
            // if the NPP results are still loading, we have to wait
            this.awaitingNppResults = true;
          } else {
            if (this.hasNoResults) {
              this.switchTabs(SearchTabType.Client);
            }
          }
        }
      }));
      this.subs.add(this.clientOrgSearchRepo.loading.subscribe((val) => {
        this.clientLoading = val;
      }));
    }

    this.orgSearchFactory = DebounceFactory.createSimple(async (
      options: PaginationOptions<SearchResult|BucketSearchResultObj|OrganizationSearchResponse>
    ) => {
      const {
        stateProvRegCode,
        countryCode
      } = this.getStateAndCountry();
      let results: APIResult<SearchResultType>;
      if (this.useBucketSearch) {
        results = await this.nonprofitService.doBucketSearch(
          options.pageNumber,
          this.searchTerm,
          stateProvRegCode,
          countryCode,
          this.rowsPerPage,
          this.clientId,
          this.charityBucketId
        );
      } else {
        results = await this.nonprofitService.doSearchPublic(
          options.pageNumber,
          this.searchTerm,
          this.rowsPerPage,
          stateProvRegCode,
          countryCode,
          this.processorType,
          this.allowedOrgLocations
        );
      }
      this.setSearchHelpers(results, SearchTabType.Standard);

      return results;
  }, 1000);
    this.orgSearchRepo = this.autoTableFactory.create({
      key: 'ORG_SEARCH',
      columns: [],
      rowsPerPage: this.rowsPerPage,
      formGroup: this.formGroup,
      tableDataFactory: this.orgSearchFactory,
      skipAddToState: true,
      userInitiatedCalls: true,
      changeDetectorRef: this.changeDetectorRef
    });
    this.subs.add(this.orgSearchRepo.activeResults$.subscribe((records: SearchResultType[]) => {
      this.handleResultsChange(records, SearchTabType.Standard);
      if (this.awaitingNppResults) {
        if (this.hasNoResults) {
          this.switchTabs(SearchTabType.Client);
        }
        this.awaitingNppResults = false;
      }
    }));
    this.subs.add(this.orgSearchRepo.loading.subscribe((val) => {
      this.loading = val;
    }));
  }

  getStateAndCountry () {
    const state = this.formGroup.value.stateProvRegCode;
    const country = this.formGroup.value.countryCode;

    return {
      stateProvRegCode: !!state && state !== ALL_SKIP_FILTER ?
        state :
        null,
      countryCode: !!country && country !== ALL_SKIP_FILTER ?
        country :
        null
    };
  }

  switchTabs (tabToSwitchTo: SearchTabType) {
    if (this.clientHasPrivateOrgs) {
      this.tabs.forEach((tab, index) => {
        tab.active = index === tabToSwitchTo;
      });
      this.onTabChange(tabToSwitchTo);
    }
  }

  handleResultsChange (
    records: SearchResultType[],
    type?: SearchTabType,
    isFavorites?: boolean
  ) {
    if (isFavorites && this.isFavoriteOrgsView) {
      this.selectedOrg = records[0];
    } else if (this.selectedSearchType === type) {
      const hasNoResultsAttr = this.selectedSearchType === SearchTabType.Client ?
        'hasNoClientResults' :
        'hasNoResults';
      if (this[hasNoResultsAttr]) {
        this.selectedOrg = null;
      } else {
        this.selectedOrg = records[0];
      }
    }
  }

  setSearchHelpers (
    results: APIResult<SearchResultType>,
    type: SearchTabType
  ) {
    this.hasStartedSearching = true;
    const records = results.data?.records ?? [];
    if (type === SearchTabType.Client) {
      this.hasNoClientResults = records.length === 0;
      if (this.hasNoClientResults) {
        this.switchTabs(SearchTabType.Standard);
      }
    } else if (type === SearchTabType.Standard) {
      this.hasNoResults = records.length === 0;
    }
  }

  onTabChange (newTabType: SearchTabType) {
    this.selectedSearchType = newTabType;
    this.handleResultsChange(this.currentRepo.currentSet, this.selectedSearchType);
  }

  async openAddOrgModal () {
    this.hide = true;
    this.addOrganizationService.setCurrentPage(AddOrgUI.AddOrgModalPages.ID_LOOKUP);
    this.addOrganizationService.setModalHeaderForAddOrg();
    this.addOrganizationService.setHelpersBasedOnCurrentPage();
    this.addOrganizationService.setClientId(this.clientId);
    this.addOrganizationService.setProcessorType(this.processorType);
    const submission = await this.addOrganizationService.openAddOrgModal(this.processorType, this.allowedOrgLocations);
    if (submission && submission.searchResult && !submission.isInvalidOrg) {
      this.initialSearch = false;
      const isPrivate = submission.selectionType === AddOrgUI.SelectionType.PRIVATE_ORG;
      const isNew = submission.selectionType === AddOrgUI.SelectionType.NEW_ORG;
      if (
        isNew &&
        !submission.searchResult.document.id &&
        this.saveNewlyCreatedOrgs
      ) {
        await this.orgSelected(submission.searchResult, true);
      } else {
        await this.orgSelected(submission.searchResult, isPrivate || isNew);
      }
    } else {
      if (submission && submission.isInvalidOrg) {
        this.handleOrgNotEligibleToSelect(submission.searchResult);
      } else {
        setTimeout(() => {
          this.hide = false;
        }, 500);
      }
    }
  }

  backToFavorites () {
    this.isFavoriteOrgsView = true;
    this.selectedOrg = this.currentRepo.currentSet[0];
  }

  async openConfirmVettingModal (adaptedOrg: AdaptedNppSearchResult) {
    this.hide = true;
    const submission = await this.addOrganizationService.openConfirmVettingModal(
      adaptedOrg,
      this.clientId,
      this.processorType,
      this.allowedOrgLocations
    );
    if (submission && submission.searchResult) {
      this.initialSearch = false;
      const isPrivate = submission.selectionType === AddOrgUI.SelectionType.PRIVATE_ORG;
      await this.orgSelected(submission.searchResult, isPrivate);
    } else {
      setTimeout(() => {
        this.hide = false;
      }, 500);
    }
  }

  setRegionSelects () {
    const countryCode = this.formGroup.value.countryCode;
    if (!!countryCode && countryCode !== ALL_SKIP_FILTER) {
      this.regionSelects = [
        {
          label: this.i18n.translate(
            'common:textAllStatesProvsRegions',
            {},
            'All states, provinces, or regions'
          ),
          value: ALL_SKIP_FILTER
        },
        ...this.countryToRegionOptionsMap[countryCode]
      ];
    } else {
      this.regionSelects = [];
    }
  } 

  setRegionsOnCountryChange () {
    this.formGroup.get('stateProvRegCode').setValue(ALL_SKIP_FILTER);
    this.setRegionSelects();
    if (!!this.orgSearchRepo) {
      this.kickOffSearch();
    }
  }

  async orgSelected (
    org: OrgUnion,
    isPrivate = false,
    isFromBucket = false
  ) {
    this.spinnerService.startSpinner();
    const {
      adaptedOrg,
      nonprofitDetail
    } = await this.nonprofitService.getAdaptedOrg(org, isPrivate, this.useBucketSearch);
    if (
      isFromBucket &&
      !this.isNomination &&
      (this.processorType === ProcessingTypes.YourCause) &&
      nonprofitDetail?.eligibleForGivingStatusId !== OrganizationEligibleForGivingStatus.ELIGIBLE
    ) {
      this.spinnerService.stopSpinner();

      return this.handleOrgNotEligibleToSelect(adaptedOrg);
    }
    if (
      !this.isNomination &&
      (this.processorType === ProcessingTypes.YourCause) &&
      this.initialSearch
    ) {
      if (adaptedOrg.document.eligibleForGivingStatusId !== OrganizationEligibleForGivingStatus.ELIGIBLE) {
        this.addOrganizationService.setSelectionType(AddOrgUI.SelectionType.UNVETTED_ORG);
        this.addOrganizationService.setOriginalSearchResponse(org);
      }
      if (
        'address' in org &&
        adaptedOrg.document.eligibleForGivingStatusId !== OrganizationEligibleForGivingStatus.ELIGIBLE
      ) {
        this.addOrganizationService.setAddOrgAttr('address', org.address);
        this.addOrganizationService.setShowContactForm(true);
        this.addOrganizationService.setSelectionType(AddOrgUI.SelectionType.PRIVATE_ORG);
      }
      // we need to create a vetting request and update language in modal
      if (adaptedOrg.document.eligibleForGivingStatusId !== OrganizationEligibleForGivingStatus.ELIGIBLE) {
        this.spinnerService.stopSpinner();
        this.openConfirmVettingModal(adaptedOrg);
      } else {
        // eligible
        this.addOrganizationService.setSelectionType(AddOrgUI.SelectionType.VETTED_ORG);
        this.spinnerService.stopSpinner();
        this.closeModal.emit({
          selectedOrg: adaptedOrg,
          isPrivate
        });
      }
    } else {
      if (isPrivate) {
        this.addOrganizationService.setSelectionType(AddOrgUI.SelectionType.NEW_ORG);
      }
      this.spinnerService.stopSpinner();
      this.closeModal.emit({
        selectedOrg: adaptedOrg,
        isPrivate
      });
    }
  }

  async handleOrgNotEligibleToSelect (selectedOrg: SearchResult) {
    this.hide = true;
    await this.modalFactory.open(
      OrgNotEligibleModalComponent,
      {
        selectedOrg
      }
    );
    this.resetRepos();
    this.formGroup.get('term').setValue('');
    this.hide = false;
  }

  kickOffSearch () {
    this.resetRepos();
  }

  resetRepos () {
    this.orgSearchRepo.reset();
    if (this.clientHasPrivateOrgs) {
      this.clientOrgSearchRepo.reset();
    }
  }

  onCancel () {
    if (this.forceOrgSelect) {
      return;
    } else {
      this.closeModal.emit();
    }
  }

  exitModal () {
    this.closeModal.emit();
    if (!!this.exitRouterLink) {
      this.router.navigate([
        this.exitRouterLink
      ]);
    }
  }

  openHelpCenter () {
    const link = this.isManager ?
      this.searchAllOrganizationsHelpLinkManager :
      this.searchAllOrganizationsHelpLinkApplicant;
    // track event
    this.heapService.track(
      TrackingEventNames.OpenHelp,
      {
        [TrackingPropertyNames.Link]: link
      }
    );
    
    window.open(link, '_blank');
  }

  ngOnDestroy () {
    this.subs.unsubscribe();
  }
}
